import {
  addToast,
  Button,
  Icon,
  Modal,
  TextAreaInput,
  TextInput,
  Select,
  SelectOptionType,
  CheckInput,
} from '@octano/global-ui';
import { useEffect, useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';
import { saveModalityRequest } from '../../../api/requests/modalities';
import { CardTitle } from '../../../components/text';
import { useLayoutState } from '../../../hooks/useLayoutState';
import { useValidations } from '../../../hooks/useValidations';
import { ModalityBody } from '../../../types/ModalityTypes';
import { ModalityCodesEnum } from '../../SectionsMaintainer/enums/modality-codes.enum';

interface Modality {
  code: string;
  name: string;
  deliveryMode: SelectOptionType | null;
  isScheduleRequired: boolean;
  description: string;
}

interface ModalityFormProps {
  isCreate?: boolean;
  editModalitiesList: Function;
  recordToEdit?: ModalityBody;
}

export const ModalityForm = ({
  isCreate,
  editModalitiesList,
  recordToEdit,
}: ModalityFormProps) => {
  const { t } = useTranslation();
  const { showErrorModal } = useLayoutState();

  const deliveryModeOptions = useMemo<SelectOptionType[]>(
    () => [
      {
        value: ModalityCodesEnum.PR,
        label: 'Presencial',
      },
      {
        value: ModalityCodesEnum.SP,
        label: 'Semipresencial',
      },
      {
        value: ModalityCodesEnum.OL,
        label: 'Online',
      },
      {
        value: ModalityCodesEnum.OA,
        label: 'Online - Autoatendido',
      },
    ],
    [],
  );

  const methods = useForm<Modality>({
    mode: 'onSubmit',
    defaultValues: {
      code: recordToEdit?.code || '',
      deliveryMode:
        deliveryModeOptions?.find(
          (e) => e.value === recordToEdit?.deliveryMode,
        ) || null,
      description: recordToEdit?.description || '',
      isScheduleRequired: recordToEdit?.isScheduleRequired || false,
    },
  });

  const [showModalityForm, setShowModalityForm] = useState<boolean>(false);
  const [wasEditedForm, setWasEditedForm] = useState<boolean>(false);

  const isEditionMode = !!recordToEdit;

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    reset,
    setError,
    setValue,
    watch,
  } = methods;

  const formValues = watch([
    'code',
    'name',
    'deliveryMode',
    'description',
    'isScheduleRequired',
  ]);

  useEffect(() => {
    if (!!recordToEdit) {
      const [
        newCode,
        newName,
        newDeliveryMode,
        newDescription,
        newIsScheduleRequired,
      ] = formValues;
      const {
        code,
        name,
        deliveryMode,
        description,
        isScheduleRequired,
      } = recordToEdit;
      const wasEditedCode = newCode !== code;
      const wasEditedName = newName !== name;
      const wasEditedDeliveryMode = newDeliveryMode?.value !== deliveryMode;
      const wasEditedDescription = newDescription !== description;
      const wasEditedIsScheduleRequired =
        newIsScheduleRequired !== isScheduleRequired;

      if (
        !wasEditedForm &&
        (wasEditedCode ||
          wasEditedName ||
          wasEditedDeliveryMode ||
          wasEditedDescription ||
          wasEditedIsScheduleRequired)
      ) {
        setWasEditedForm(true);
      } else if (
        wasEditedForm &&
        !wasEditedCode &&
        !wasEditedName &&
        !wasEditedDeliveryMode &&
        !wasEditedDescription &&
        !wasEditedIsScheduleRequired
      ) {
        setWasEditedForm(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  useEffect(() => {
    if (showModalityForm && recordToEdit) {
      setValue('code', recordToEdit.code);
      setValue('name', recordToEdit.name);
      setValue('isScheduleRequired', recordToEdit.isScheduleRequired);
      setValue(
        'deliveryMode',
        deliveryModeOptions?.find(
          (e) => e.value === recordToEdit?.deliveryMode,
        ) || null,
      );
      if (recordToEdit.description) {
        setValue('description', recordToEdit.description);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModalityForm, deliveryModeOptions]);

  const { validateTextNotEmpty } = useValidations();

  const onSubmit = async (formValues: Modality) => {
    const requestBody = {
      ...formValues,
      ...(isEditionMode && recordToEdit ? { id: recordToEdit.id } : {}),
    };

    const { data, error } = await saveModalityRequest({
      ...requestBody,
      deliveryMode: requestBody?.deliveryMode?.value as ModalityCodesEnum,
    });
    if (error && error.code === 'CONNECTION') {
      showErrorModal();
    } else if (error && error.code === 'HTTP_ERROR') {
      if (error.status === 400) {
        setError('code', {
          message: t('common.errors.codeError', { code: formValues.code }),
        });
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('common.errors.save'),
        });
      }
    } else if (data) {
      editModalitiesList(data.data);
      closeModal();
      addToast({
        icon: 'success',
        color: 'success',
        text: t(
          isEditionMode
            ? 'common.messages.editSuccess'
            : 'modality.createSuccess',
        ),
      });
    }
  };

  const handleEditClick = () => {
    setShowModalityForm(true);
  };

  const closeModal = () => {
    setShowModalityForm(false);
    setWasEditedForm(false);
    reset();
  };

  return (
    <>
      {isCreate ? (
        <Button
          text={t('modality.createTitle')}
          type="button"
          size="sm"
          icon="plus"
          className="g-add-button mb-3 mt-3"
          onClick={() => setShowModalityForm(true)}
        />
      ) : (
        <span onClick={handleEditClick} className="cursor-pointer">
          <Icon name="edit" key="edit" color="secondary" />
        </span>
      )}

      <Modal
        isOpen={showModalityForm}
        toggle={() => {
          setShowModalityForm(!showModalityForm);
        }}
        size="lg"
      >
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col>
                <div className="text-center">
                  <CardTitle
                    children={t(
                      isEditionMode
                        ? 'modality.formEditTitle'
                        : 'modality.createTitle',
                    )}
                  />
                </div>
                <Row className="mt-0 mt-md-5">
                  <Col xs="12" md="6">
                    <TextInput
                      name="code"
                      label={t('common.forms.code')}
                      control={control}
                      rules={{
                        validate: validateTextNotEmpty,
                        pattern: {
                          value: /^[0-9a-zA-Z]+$/,
                          message: t('common.validations.invalidAlphanumeric'),
                        },
                      }}
                    />
                  </Col>

                  <Col xs="12" md="6">
                    <TextInput
                      name="name"
                      label={t('common.forms.name')}
                      control={control}
                      rules={{
                        validate: validateTextNotEmpty,
                        minLength: {
                          value: 3,
                          message: t('common.validations.minLength', {
                            length: 3,
                          }),
                        },
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs="12" md="12">
                    <Select
                      name="deliveryMode"
                      options={deliveryModeOptions}
                      label="Tipo de modalidad"
                      control={control}
                      rules={{
                        validate: validateTextNotEmpty,
                      }}
                    />
                  </Col>
                  <Col xs="12" md="12">
                    <CheckInput
                      name="isScheduleRequired"
                      label="¿Requiere horario?"
                      control={control}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <TextAreaInput
                      name="description"
                      label={`${t('common.forms.description')} ${t(
                        'common.forms.optionalLabel',
                      )}`}
                      control={control}
                      height={200}
                      placeholder={t(`common.forms.descriptionPlaceholder`)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col className="mt-5">
                <Row>
                  <Col xs={{ size: 12, order: 2 }} md={{ size: 6, order: 1 }}>
                    <Button
                      type="button"
                      text={t('common.actions.cancel')}
                      outlined
                      onClick={closeModal}
                      fullwidth
                    />
                  </Col>
                  <Col xs={{ size: 12, order: 1 }} md={{ size: 6, order: 2 }}>
                    <Button
                      type="submit"
                      text={t(
                        isEditionMode
                          ? 'common.actions.saveChanges'
                          : 'modality.create',
                      )}
                      loading={isSubmitting}
                      fullwidth
                      className="mb-3 mb-md-0"
                      disabled={isEditionMode && !wasEditedForm}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </FormProvider>
      </Modal>
    </>
  );
};
